import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardGridItem from 'assets/components/card/themes/Theme.Card.GridItem';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import { isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { first, isEmpty, keys, sortBy } from 'lodash';
import ClaimDocumentForm from 'module/public-web/views/claimSubmit/components/Claim.DocumentForm';
import { useEffect, useState } from 'react';
import ClaimDocumentPreview from '../other/Claim.DocumentPreview';
import ClaimJobDataPointsInputs from '../other/Claim.Job.DataPoints.Inputs';
import useClaimSimpleClaim from './Claim.SimpleClaim.Hooks';

export default function ClaimSimpleClaimForm(props: { contract: Model.IContract; onClose: () => void }) {
  const { lang } = getLocales();
  const { form, claimTypes, onlyClaimType, claimTypeValue, uploadableDocuments } = useClaimSimpleClaim(props);
  const claimJobForUpdate = first(form.data?.claimJobs);
  function updateClaimJob(claimJob: Utils.FormData<Model.IClaimJob>) {
    form.merge({ claimJobs: [{ ...claimJob }] });
  }

  //#region Documents
  const [document, selectDocument] = useState<Model.IClaimTypeDocument>();
  const [documentsFiles, setDocumentsFiles] = useState<Record<number, Model.IUploadableClaimJobDocument[]>>({});
  const handleFileChange = (files: File[]) => {
    const updatedFiles = files.map((file) => ({
      formFile: file,
      newName: file.name,
      notes: '',
    }));
    setDocumentsFiles((prev) => ({
      ...prev,
      [document.id]: updatedFiles,
    }));
    docForm.update({ formFiles: updatedFiles });
  };
  useEffect(() => {
    selectDocument(uploadableDocuments[0]);
  }, [uploadableDocuments]);
  const docForm = useForm<Model.IClaimJobDocument & { formFiles: Model.IUploadableClaimJobDocument[] }>(
    {
      default: !document
        ? {}
        : {
            name: document.name,
            claimTypeDocumentId: document.id,
            approved: document.approvalRequired ? false : true,
          },
      validation: (data, errors) => {
        errors.formFiles = {};
        if (!data.claimTypeDocumentId) {
          errors.claimTypeDocumentId = lang.required;
        }
        if (document) {
          const hasFiles = !!documentsFiles[document.id]?.length;
          if (document.required && !hasFiles) {
            errors.formFiles._objectError = `${lang.required} for ${document.name}`;
          }
          if (hasFiles) {
            documentsFiles[document.id]?.forEach((formFile, index) => {
              if (!formFile.newName) {
                errors.formFiles[index] = errors.formFiles[index] || {};
                errors.formFiles[index].newName = lang.required;
              }
            });
          }
        }
      },
      onSubmit: async (data) => {
        const documentKeys = keys(documentsFiles) || [];
        for (const key of documentKeys) {
          const claimTypeDoc = uploadableDocuments?.find((it) => it.id === Number(key));
          const filesForUpload = documentsFiles[key] || [];
          const updatedClaimJobs = [...(form.data?.claimJobs || [])];
          const newClaimJobDocumentsForUpload = filesForUpload?.map((file) => ({
            ...data,
            approved: claimTypeDoc.approvalRequired ? false : true,
            claimTypeDocumentId: claimTypeDoc.id,
            formFile: file.formFile,
            notes: isValidValue(file.notes) ? file.notes : undefined,
            name: file.newName,
            claimJobId: claimJobForUpdate?.id,
          }));

          first(updatedClaimJobs).claimJobDocuments = [
            ...(first(updatedClaimJobs)?.claimJobDocuments ?? []),
            ...newClaimJobDocumentsForUpload,
          ];

          form.merge({ claimJobs: updatedClaimJobs });
          setDocumentsFiles({});
        }
      },
    },
    [document]
  );
  const currentFiles = documentsFiles[document?.id] || [];
  //#endregion

  //#region DataPoints
  const updateValue = (value: string, id: number) => {
    const updatedDataPoints = first(form.data?.claimJobs)?.dataPoints?.map((item) =>
      item.claimTypeDataPointId === id ? { ...item, value } : item
    );
    updateClaimJob({ ...first(form.data?.claimJobs), dataPoints: updatedDataPoints });
  };
  useEffect(() => {
    const updatedClaimJob = first(form.data?.claimJobs);
    const newDataPoints = sortBy(
      claimTypes
        ?.find((it) => it.id === claimJobForUpdate?.claimTypeId)
        ?.claimTypeDataPoints?.map((it) => ({
          claimTypeDataPointId: it.id,
          claimTypeId: it.claimTypeId,
          name: it.name,
          type: it.type,
          version: it.version,
          isRequired: it.isRequired,
          value: undefined,
        })),
      (it) => it.claimTypeDataPointId
    );

    updatedClaimJob.dataPoints = newDataPoints;

    updateClaimJob({ ...updatedClaimJob });
  }, [first(form.data?.claimJobs)?.claimTypeId]);
  //#endregion

  return (
    <Modal
      subcomponents={ThemeModalDefault}
      header={lang.createSimpleClaim}
      onClose={props.onClose}
      render={
        <FormWrapper>
          {!onlyClaimType && (
            <SelectInput
              name="claimTypeId"
              label={lang.claimType}
              data={claimTypes?.map((it) => it.displayInfo)}
              value={claimTypeValue}
              onChange={({ claimTypeId }) => updateClaimJob({ ...claimJobForUpdate, claimTypeId })}
              icon={<Icon class="fas-clipboard-list" />}
            />
          )}
          <MultilineTextInput
            name="customerComplaint"
            label={lang.customerComplaint}
            value={claimJobForUpdate?.customerComplaint}
            onChange={({ customerComplaint }) => updateClaimJob({ ...claimJobForUpdate, customerComplaint })}
          />
          <MultilineTextInput
            name="cause"
            label={lang.causeOfFailure}
            value={claimJobForUpdate?.cause}
            onChange={({ cause }) => updateClaimJob({ ...claimJobForUpdate, cause })}
          />
          <MultilineTextInput
            name="correction"
            label={lang.correctionDetails}
            value={claimJobForUpdate?.correction}
            onChange={({ correction }) => updateClaimJob({ ...claimJobForUpdate, correction })}
          />
          {!!uploadableDocuments?.length && (
            <div style={{ marginLeft: Theme.Size.L, marginRight: Theme.Size.L }}>
              <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>
                {`${lang.claim} ${lang.documents}`}
                <span>
                  <SelectInput
                    name="docId"
                    style={{
                      formInput: {
                        maxWidth: Theme.Size.pixel(240),
                      },
                    }}
                    placeholder={`${lang.claim} ${lang.documents} ...`}
                    data={uploadableDocuments?.map((document) => ({
                      id: document.id,
                      title: combineStrings(' - ', document.name, document.source),
                    }))}
                    value={document?.id}
                    onChange={({ docId }) => selectDocument(uploadableDocuments.find((it) => it.id === docId))}
                    icon={<Icon class="fas-file" />}
                  />
                </span>
              </h2>
              {!isEmpty(
                form?.data?.uploadableClaimTypeDocuments?.filter(
                  (doc) =>
                    doc.required &&
                    !new Set(
                      first(form?.data?.claimJobs)?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId)
                    )?.has(doc.id)
                )
              ) &&
                uploadableDocuments.some((doc) => doc.required && isEmpty(documentsFiles[doc.id])) && (
                  <p
                    style={{
                      backgroundColor: Theme.Color.error,
                      color: 'white',
                      padding: `${Theme.Size.M} ${Theme.Size.L}`,
                      fontWeight: 'bold',
                    }}
                  >
                    {`${lang.required} files for the following documents: ${uploadableDocuments
                      .filter((doc) => doc.required && isEmpty(documentsFiles[doc.id]))
                      .map((doc) => doc.name)
                      .join(', ')}`}
                  </p>
                )}
              <ClaimDocumentPreview
                documents={first(form.data?.claimJobs)?.claimJobDocuments?.map((it, docIdx) => ({
                  id: it.id,
                  name: it.name,
                  onDelete: () => {
                    const tempDocs = [...(first(form.data?.claimJobs)?.claimJobDocuments || [])];
                    const newDocs = tempDocs?.filter((_, tempIdx) => docIdx !== tempIdx);
                    const updatedClaimJobs = [...(form.data?.claimJobs || [])];
                    first(updatedClaimJobs).claimJobDocuments = [...newDocs];
                    form.merge({ claimJobs: updatedClaimJobs });
                  },
                }))}
              />
              <br />
              <FileInput
                name="files"
                fileLimit={document?.allowMultiple ? 5 : 1}
                value={currentFiles.map((it) => ({
                  original: it.formFile,
                  type: it?.formFile?.type as Utils.File.MimeType,
                  dataUrl: window.URL.createObjectURL(it.formFile),
                  newName: it.newName,
                  notes: it.notes,
                }))}
                onChange={({ files }) => handleFileChange(files.map((f) => f.original))}
                class={ThemeFileInputDropzone}
                disabled={!document}
                error={docForm.errors.formFiles?._objectError}
                renderBefore={
                  !document ? (
                    lang.youNeedToSelectOneDocument
                  ) : (
                    <Card
                      class={ThemeCardGridItem}
                      style={{
                        cardMedia: { width: 0, height: 0 },
                        card: { width: '400px', borderRadius: '10px' },
                        cardActions: { marginBottom: '5px' },
                      }}
                      actions={
                        document.example && (
                          <LinkButton
                            class={ThemeButtonCircle}
                            style={{ fontSize: Theme.FontSize.M, margin: 0 }}
                            url={document.example}
                            media={<Icon class="fas-external-link-alt" />}
                            render={lang.example}
                            target="_blank"
                          />
                        )
                      }
                      info={document.source}
                      title={combineStrings(' - ', document.name)}
                      description={document.description}
                    />
                  )
                }
                renderAfter={currentFiles.map((file, index) => (
                  <div key={index}>
                    <h6>{file.formFile.name}</h6>
                    <TextInput
                      name="newName"
                      label={lang.name}
                      value={file.newName}
                      onChange={({ newName }) => {
                        const updatedFiles = [...currentFiles];
                        updatedFiles[index].newName = newName;
                        setDocumentsFiles((prev) => ({
                          ...prev,
                          [document.id]: updatedFiles,
                        }));
                      }}
                      error={docForm.errors?.formFiles?.[index]?.newName}
                    />
                    {document?.allowNotes && (
                      <MultilineTextInput
                        name="notes"
                        label={lang.notes}
                        value={file.notes}
                        onChange={({ notes }) => {
                          const updatedFiles = [...currentFiles];
                          updatedFiles[index].notes = notes;
                          setDocumentsFiles((prev) => ({
                            ...prev,
                            [document.id]: updatedFiles,
                          }));
                        }}
                      />
                    )}
                  </div>
                ))}
              />
              <br />
              <Button
                render={lang.add}
                media={<Icon class="fas-upload" />}
                onClick={docForm.submit}
                disabled={
                  docForm.hasErrors ||
                  !document ||
                  uploadableDocuments.some((doc) => doc.required && isEmpty(documentsFiles[doc.id]))
                }
                active={!docForm.hasErrors}
                class={ThemeButtonCircle}
                style={{ marginLeft: 'auto' }}
              />
              <br />
            </div>
          )}
          {!!first(form.data?.claimJobs)?.dataPoints?.length && (
            <div style={{ marginLeft: Theme.Size.L, marginRight: Theme.Size.L }}>
              <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.details}</h2>
              <ClaimJobDataPointsInputs dataPoints={first(form.data?.claimJobs)?.dataPoints} onUpdate={updateValue} />
            </div>
          )}
          <h2 style={{ display: 'flex', alignItems: 'center' }}>
            {lang.documents}
            <Button
              class={ThemeButtonCircle}
              style={{ marginLeft: 'auto', fontSize: Theme.FontSize.M }}
              render={form.data.isAddingNewDocument ? lang.cancel : lang.add}
              media={<Icon class={form.data.isAddingNewDocument ? 'fas-ban' : 'fas-plus'} />}
              data={{ isAddingNewDocument: !form.data.isAddingNewDocument }}
              onClick={form.update}
              active={form.data.isAddingNewDocument}
              htmlElementProps={{ title: form.data.isAddingNewDocument ? lang.ban : lang.add }}
            />
          </h2>
          <ClaimDocumentPreview
            documents={[
              ...form.data.claimDocuments.map((it, docIdx) => ({
                id: it.id,
                url: it.mediaFile?.url,
                name: it?.name,
                extension: it.mediaFile?.extension,
                description: it?.notes,
                info: lang.new,
                onDelete: () => {
                  const tempDocs = [...(form.data?.claimDocuments || [])];
                  const newDocs = tempDocs?.filter((_, tempIdx) => docIdx !== tempIdx);
                  form.update({ claimDocuments: newDocs });
                },
              })),
            ]}
          />
          <br />
          {form.data.isAddingNewDocument && (
            <ClaimDocumentForm
              onSubmit={(data) => form.update({ claimDocuments: [...form.data.claimDocuments, ...data] })}
            />
          )}
        </FormWrapper>
      }
      footer={
        <>
          <Button
            onClick={form.submit}
            media={<Icon class="fas-save" />}
            render={lang.save}
            active={!form.hasErrors && form.isUpdated}
            disabled={form.hasErrors || !form.isUpdated}
            isLoading={form.isSubmitted}
            class={ThemeButtonCircle}
            htmlElementProps={{ title: lang.save }}
          />
        </>
      }
    />
  );
}
